import React from 'react';
import { View } from 'react-native';
import Svg, { Defs, LinearGradient, Stop, Text as SvgText } from 'react-native-svg';

import { getFontFamily, useThemePalette } from '@/config/theme';
import { useLocale } from '@/lib/i18n/LocaleProvider';

/**
 * The "Tashkeela" wordmark painted with the logo's own neon gradient
 * (cyan -> volt -> violet) instead of flat white, so the header reads as one
 * brand with the mark. It is ALWAYS the Latin brand name (matching the logo
 * artwork). The SVG is wrapped in a `direction: 'ltr'` view because inside the
 * Arabic RTL build an SVG inherits `direction: rtl`, which flips the text anchor
 * and clips the wordmark off-canvas - forcing LTR keeps it rendering in both
 * locales. SVG keeps the gradient crisp on web/Android.
 */
export function BrandWordmark({ text = 'Tashkeela', size = 22 }: { text?: string; size?: number }) {
  const palette = useThemePalette();
  const { locale } = useLocale();
  // `true` forces the Latin display face regardless of locale.
  const fontFamily = getFontFamily(locale, 'bold', true);
  // 0.64 ~= average glyph advance for this bold display face at the given size.
  const width = Math.ceil(text.length * size * 0.64) + 6;
  const height = Math.ceil(size * 1.32);

  return (
    <View style={{ direction: 'ltr', width, height }}>
      <Svg width={width} height={height} accessibilityLabel={text}>
        <Defs>
          <LinearGradient id="brandWordmark" x1="0" y1="0" x2="1" y2="0.35">
            <Stop offset="0" stopColor={palette.voltBright} />
            <Stop offset="0.5" stopColor={palette.volt} />
            <Stop offset="1" stopColor={palette.brandViolet} />
          </LinearGradient>
        </Defs>
        <SvgText
          x={3}
          y={size}
          fontSize={size}
          fontWeight="bold"
          fontFamily={fontFamily}
          fill="url(#brandWordmark)"
          textAnchor="start"
        >
          {text}
        </SvgText>
      </Svg>
    </View>
  );
}
